// src/features/videos/filterVideos.ts — pure chip filtering for the Videos page.
import { normHandle } from '@/core/stores/followStore';
import type { VideoItem, VideoChipId, VideoCategory } from '@/core/models/video';

const CATEGORY_CHIPS = new Set<VideoChipId>(['tech', 'science', 'news', 'gaming', 'education', 'music']);

export interface VideoFilterOptions {
  chip: VideoChipId;
  /** Normalized (no leading @, lowercased) handles the current user follows. */
  followedHandles: Set<string>;
  /** authorId (users-registry key) -> handle, for the 'following' chip. */
  creatorHandles: Record<string, string>;
}

function isCategory(chip: VideoChipId): chip is VideoCategory {
  return CATEGORY_CHIPS.has(chip);
}

/** Pure: filter + sort videos by the active chip. Never mutates input. */
export function filterVideos(items: VideoItem[], opts: VideoFilterOptions): VideoItem[] {
  let filtered: VideoItem[];

  if (opts.chip === 'following') {
    filtered = items.filter((v) => {
      const handle = opts.creatorHandles[v.authorId];
      return handle !== undefined && opts.followedHandles.has(normHandle(handle));
    });
  } else if (opts.chip === 'trending') {
    filtered = items.filter((v) => v.trendingRank !== undefined);
  } else if (opts.chip === 'for-you') {
    // Excludes items already ranked in "Trending now" — the two rows render on
    // the same page at once, so without this "for you" was just a re-sort of
    // the same trending set (fully overlapping cards).
    filtered = items.filter((v) => v.trendingRank === undefined);
  } else if (isCategory(opts.chip)) {
    filtered = items.filter((v) => v.category === opts.chip);
  } else {
    // 'latest' — no additional filter, ordering carries the difference.
    filtered = items;
  }

  const sorted = [...filtered];
  if (opts.chip === 'trending') {
    sorted.sort((a, b) => (a.trendingRank ?? 0) - (b.trendingRank ?? 0));
  } else if (opts.chip === 'for-you') {
    // Engagement-weighted, not recency — a genuinely different order from
    // 'latest' rather than the same ts-desc sort under two chip names.
    sorted.sort((a, b) => b.views - a.views);
  } else {
    sorted.sort((a, b) => b.ts - a.ts);
  }
  return sorted;
}
